/**
 * The one banner the Prospects page may show above the list, for something wrong that the advisor
 * needs to act on: the CRM is disconnected, the last sync stopped early, or a field the whole list
 * depends on is not mapped. Which one, if any, is decided in `lib/book-notices`.
 *
 * It is kept to two lines with its action beside the text, so even when it shows the list stays
 * near the top of the page. The CRM is named from the book's own provider.
 */
import type { ReactNode } from 'react';
import { TriangleAlert } from 'lucide-react';
import { Alert, AlertDescription, Button } from '@design-system';
import type { Prospecting } from '@client/api/types';
import type { BookNotices } from '../lib/book-notices';
import { crmCopy } from '../lib/crm-copy';
import { CONFIDENCE_LABELS, formatDate } from '../lib/labels';

interface BannerProps {
  title: string;
  children: ReactNode;
  action?: { label: string; onClick: () => void };
}

function Banner({ title, children, action }: BannerProps) {
  return (
    <Alert variant="warning">
      <TriangleAlert />
      <AlertDescription>
        <div className="flex w-full flex-col gap-2 md:flex-row md:items-center md:justify-between md:gap-6">
          <div>
            <p className="font-medium">{title}</p>
            {children}
          </div>
          {action && (
            <Button
              variant="outline"
              size="sm"
              className="shrink-0 self-start md:self-center"
              onClick={action.onClick}
            >
              {action.label}
            </Button>
          )}
        </div>
      </AlertDescription>
    </Alert>
  );
}

interface BookBannerProps {
  book: Prospecting.BookStatus;
  notices: BookNotices;
  onOpenFieldMapping: () => void;
  onOpenIntegrations: () => void;
}

export function BookBanner({ book, notices, onOpenFieldMapping, onOpenIntegrations }: BookBannerProps) {
  const copy = crmCopy(book.provider);

  if (notices.banner === 'disconnected') {
    return (
      <Banner
        title={`${copy.name} is disconnected`}
        action={{ label: `Reconnect ${copy.name}`, onClick: onOpenIntegrations }}
      >
        <p>This list is from your last sync and is no longer being refreshed.</p>
      </Banner>
    );
  }

  if (notices.banner === 'lastSyncIncomplete') {
    return (
      <Banner title="Your last sync did not finish">
        <p>
          It stopped before reading all your contacts on {formatDate(book.freshness.lastSyncAt)}, so some may be
          missing from this list until the next sync completes.
        </p>
      </Banner>
    );
  }

  if (notices.banner === 'essentialFieldUnmapped') {
    return (
      <Banner
        title="A field this list depends on is not mapped"
        action={{ label: 'Open field mapping', onClick: onOpenFieldMapping }}
      >
        {notices.unmappedEssentials.map(reason => (
          <p key={reason}>{CONFIDENCE_LABELS[reason]}</p>
        ))}
      </Banner>
    );
  }

  return null;
}
